import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

export const SITE_URL = "https://www.ntsticks.com";

/**
 * Per-route metadata.
 *
 * public/index.html carries a single static `<link rel="canonical">` and
 * `og:url`, both hardcoded to the homepage. In a single-page app that shell is
 * served for every route, so /shop, /wholesale and all 32 product pages were
 * each telling Google "the real version of this page is the homepage" — the
 * strongest available instruction to drop a page from the index. Nothing in the
 * app overrode it.
 *
 * Rendering this component on a page emits a canonical and og:url for the URL
 * actually being viewed. react-helmet keeps the last-mounted tag, and page
 * components mount below App, so a page's values win over the defaults there.
 *
 * `image` should be an absolute path from the site root ("/assets/nt/x.png").
 */
export default function Seo({
  title,
  description,
  image,
  noindex = false,
  canonicalPath,
  children,
}) {
  const { pathname } = useLocation();

  // `canonicalPath` points a page at a different URL as its indexable version.
  // /shop?category=dhoop-sticks uses it to name /dhoop-sticks instead: the two
  // show the same products, and left alone they compete for the same query
  // while splitting the ranking signals between them.
  const raw = canonicalPath || pathname;

  // Collapse the trailing slash so /shop and /shop/ don't self-compete.
  const path = raw !== "/" && raw.endsWith("/") ? raw.slice(0, -1) : raw;
  const url = `${SITE_URL}${path}`;
  const absoluteImage = image
    ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
    : `${SITE_URL}/assets/nt/ntsticklogo.webp`;

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}

      <link rel="canonical" href={url} />
      <meta property="og:url" content={url} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={absoluteImage} />

      <meta name="twitter:url" content={url} />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={absoluteImage} />

      {/* Cart, login, order history and the staff dashboard have nothing to
          offer a search result, and robots.txt already disallows them. */}
      {noindex && <meta name="robots" content="noindex, follow" />}

      {children}
    </Helmet>
  );
}
